import {
  Alert as AlertVaultAlert,
  AlertActivityPage,
  AlertAggregate as GeneratedAlertVaultAggregate,
  AlertDeliveryPage,
  AlertGroupPage,
  AlertSummary,
  AlertVaultClient as GeneratedAlertVaultClient,
  AlertVaultClientInput,
  AlertWorkflow as GeneratedAlertVaultWorkflow,
  AlertWorkflowInput,
  DeliveryInput,
  MutateAlertLifecycleRequest,
  Ticket,
  TicketInput,
  TicketPage,
  TicketUpdate,
  WorkflowClientBinding,
  WorkflowCondition,
  WorkflowPolicyScope,
} from '@ariksa/alert-vault';
import { AlertResponse } from '@ariksa/notification/api';
import flagsmith from '@flagsmith/flagsmith';
import { v4 as uuidv4 } from 'uuid';

import { AlertVaultService } from '../services';

type AlertVaultAggregate = GeneratedAlertVaultAggregate;

export const alertVaultFeatureFlag = 'alert_vault_feature' as const;

const alertCategoryClasses = [
  'Cloud Posture',
  'Data Security',
  'Identity Authorization',
  'Vulnerabilities',
] as const;

export const isAlertVaultEnabled = () =>
  flagsmith.hasFeature(alertVaultFeatureFlag);

export interface AlertVaultClient {
  id: string;
  name: string;
  provider: string;
  status: boolean;
  description?: string;
  credential_ref?: string;
  config?: Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
}

export interface AlertVaultWorkflow {
  id: string;
  name: string;
  status: boolean;
  cloud: string;
  trigger_quantifier: string;
  triggers: string[];
  condition_quantifier: string;
  notification_interval_seconds?: number;
  conditions?: Array<Record<string, unknown>>;
  policies?: Array<Record<string, unknown>>;
  clients?: Array<Record<string, unknown>>;
  description?: string;
  created_at?: string;
  updated_at?: string;
}

export interface AlertVaultAlertQuery {
  alertId?: string;
  accountId?: string;
  environmentId?: string;
  resourceType?: string;
  category?: string;
  categoryClass?: string;
  policyId?: string;
  // PolicyHub finding group, used to list the alerts behind a finding row.
  groupId?: string;
  // Server-side order (last_seen, severity, entity_name, resource_type).
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  severity?: string | string[];
  status?: string;
  search?: string;
  from?: string;
  to?: string;
  pageNumber?: number;
  pageSize?: number;
  cursor?: string;
  groupBy?: string;
  clientId?: string;
}

export interface AlertVaultPage<T> {
  items: T[];
  next_cursor?: string;
  // Exact match count; alert lists report it from 0.1.12.
  total?: number;
}

// The existing table component is page-number based, while Alert Vault is
// cursor based. Keep the cursor chain per filter set so the UI still renders
// its existing pagination without turning the backend back into an offset API.
const cursorChains = new Map<string, Map<number, string>>();

const filterKey = (namespace: string, input: object) => {
  const values = input as Record<string, unknown>;
  return `${namespace}:${JSON.stringify(
    Object.keys(values)
      .filter(key => !['pageNumber', 'cursor'].includes(key))
      .sort()
      .reduce((result, key) => {
        result[key] = values[key];
        return result;
      }, {} as Record<string, unknown>),
  )}`;
};

const cursorForPage = (namespace: string, input: AlertVaultAlertQuery) => {
  if (input.cursor) return input.cursor;
  const page = input.pageNumber ?? 1;
  if (page <= 1) return undefined;
  return cursorChains.get(filterKey(namespace, input))?.get(page - 1);
};

const rememberNextCursor = (
  namespace: string,
  input: AlertVaultAlertQuery,
  nextCursor?: string,
) => {
  const page = input.pageNumber ?? 1;
  const key = filterKey(namespace, input);
  const chain = cursorChains.get(key) ?? new Map<number, string>();
  if (nextCursor) chain.set(page, nextCursor);
  else chain.delete(page);
  cursorChains.set(key, chain);
};

const alertListParams = (input: AlertVaultAlertQuery) => {
  const params = {
    ...(input.groupId ? { group_id: input.groupId } : {}),
    ...(input.sortBy
      ? { sort_by: input.sortBy, sort_order: input.sortOrder }
      : {}),
  };
  return Object.keys(params).length ? { params } : undefined;
};

// Prefer the server's exact alert count; older servers fall back to the
// lower-bound estimate.
const alertPageTotal = (
  input: AlertVaultAlertQuery,
  page: AlertVaultPage<unknown>,
) =>
  typeof page.total === 'number'
    ? page.total
    : pageTotal(input, page.items.length, !!page.next_cursor);

const pageTotal = (
  input: AlertVaultAlertQuery,
  itemCount: number,
  hasNext: boolean,
) => {
  const page = input.pageNumber ?? 1;
  const size = input.pageSize ?? 50;
  // Cursor endpoints intentionally do not perform a count query. This is a
  // lower-bound estimate used only by the legacy page-number widget; the
  // cursor chain remains the source of truth for fetching the next page.
  return Math.max((page - 1) * size + itemCount + (hasNext ? 1 : 0), itemCount);
};

const providerName = (provider: string) =>
  provider === 'msteams'
    ? 'teams'
    : provider === 'elasticsearch'
    ? 'elasticsearch'
    : provider;

const providerConfigKey = (provider: string) =>
  provider === 'servicenow'
    ? 'service_now'
    : provider === 'elasticsearch'
    ? 'elastic_search'
    : provider === 'msteams'
    ? 'teams'
    : provider;

const canonicalProviderName = (provider?: string | null) =>
  provider === 'service_now'
    ? 'servicenow'
    : provider === 'elastic_search'
    ? 'elasticsearch'
    : provider;

const legacyEnumValue = (value?: string | null) =>
  value ? value.toUpperCase() : value;

const responseData = <T>(response: { data: T }) => response.data;

const legacyClientProvider = (provider: string) =>
  provider === 'service_now'
    ? 'servicenow'
    : provider === 'elastic_search'
    ? 'elasticsearch'
    : provider;

const toAlertVaultClientInput = (request: any): AlertVaultClientInput => {
  const config = request?.clientConfig ?? request?.editClientConfig ?? request;
  const provider = legacyClientProvider(
    config?.client_name ?? config?.provider,
  );
  const providerConfig =
    config?.[provider] ??
    (provider === 'servicenow' ? config?.service_now : undefined) ??
    (provider === 'elasticsearch' ? config?.elastic_search : undefined) ??
    {};
  const credentialRef =
    config?.credential_ref ?? providerConfig?.credential_ref;
  if (!credentialRef) {
    throw new Error(
      'Alert Vault client provisioning requires an external credential_ref',
    );
  }
  return {
    name: config.name,
    provider: provider as AlertVaultClientInput['provider'],
    status: config.status ?? true,
    description: config.description ?? undefined,
    credential_ref: credentialRef,
    config: config.config ?? {},
  };
};

const toAlertVaultWorkflowInput = (
  request: any,
  clients: GeneratedAlertVaultClient[],
): AlertWorkflowInput => {
  const workflow =
    request?.alertWorkflowRequest ?? request?.editAlertWorkflow ?? request;
  const clientsByName = new Map(clients.map(client => [client.name, client]));
  const bindings = (workflow.clients ?? []).flatMap((group: any) =>
    (group.client_list ?? []).flatMap((entry: any) => {
      const client =
        clientsByName.get(entry.name) ?? clientsByName.get(entry.client_name);
      return client
        ? [{ client_id: client.id, config: entry.client_config ?? {} }]
        : [];
    }),
  );
  const conditions = (
    workflow.alert_conditions ??
    workflow.conditions ??
    []
  ).map((condition: any) => ({
    entity: condition.name ?? condition.entity,
    values: condition.entity_list ?? condition.values ?? [],
    ...(condition.tag_key ? { tag_key: condition.tag_key } : {}),
  }));
  return {
    name: workflow.name,
    status: workflow.status ?? true,
    cloud: workflow.cloud,
    trigger_quantifier: workflow.trigger_quantifier,
    triggers: workflow.trigger ?? workflow.triggers ?? [],
    condition_quantifier: workflow.condition_quantifier,
    notification_interval_seconds: Number(workflow.notification_interval ?? 0),
    conditions,
    policies: (workflow.policy ?? workflow.policies ?? []).flatMap(
      (policy: any) =>
        (policy.environment_ids ?? [policy.environment_id]).map(
          (environmentId: string) => ({
            policy_id: policy.policy_id,
            environment_id: environmentId,
            account_ids:
              policy.account_ids?.find?.(
                (accounts: any) => accounts.environment_id === environmentId,
              )?.account_ids ?? policy.account_ids,
          }),
        ),
    ),
    clients: bindings,
    description: workflow.description ?? undefined,
  };
};

const mapClientToNotification = (
  client: GeneratedAlertVaultClient | AlertVaultClient,
): Record<string, unknown> => {
  const provider = providerName(client.provider);
  const configKey = providerConfigKey(client.provider);
  const config = client.config ?? {};

  return {
    uuid: client.id,
    name: client.name,
    client_name: provider,
    status: client.status,
    description: client.description ?? undefined,
    // Existing workflow and integration components read provider config from
    // the legacy provider key. Alert Vault keeps it as a generic config map.
    [configKey]: config,
    ...config,
  };
};

export { mapClientToNotification };

const mapWorkflowCondition = (condition: WorkflowCondition) => ({
  name: condition.entity,
  entity_list: condition.values,
  ...(condition.tag_key ? { tag_key: condition.tag_key } : {}),
});

const mapWorkflowPolicy = (policy: WorkflowPolicyScope) => ({
  policy_id: policy.policy_id,
  environment_ids: [policy.environment_id],
  ...(policy.account_ids?.length
    ? {
        account_ids: [
          {
            environment_id: policy.environment_id,
            account_ids: policy.account_ids,
          },
        ],
      }
    : {}),
});

const mapWorkflowClient = (
  binding: WorkflowClientBinding,
  clientsById: Map<string, GeneratedAlertVaultClient>,
) => {
  const client = clientsById.get(binding.client_id);
  const provider = client ? providerName(client.provider) : undefined;
  const configKey = client ? providerConfigKey(client.provider) : undefined;
  const config = binding.config ?? client?.config ?? {};

  return {
    uuid: binding.client_id,
    name: client?.name ?? binding.client_id,
    ...(provider ? { client_name: provider } : {}),
    status: client?.status,
    description: client?.description,
    config_details: config,
    ...(configKey ? { [configKey]: config } : {}),
  };
};

const mapWorkflowToNotification = (
  workflow: GeneratedAlertVaultWorkflow,
  clientsById = new Map<string, GeneratedAlertVaultClient>(),
): Record<string, unknown> => ({
  ...workflow,
  uuid: workflow.id,
  trigger: workflow.triggers,
  notification_interval: String(workflow.notification_interval_seconds ?? 0),
  conditions: (workflow.conditions ?? []).map(mapWorkflowCondition),
  alert_conditions: (workflow.conditions ?? []).map(mapWorkflowCondition),
  policy: (workflow.policies ?? []).map(mapWorkflowPolicy),
  clients: (workflow.clients ?? []).map(binding =>
    mapWorkflowClient(binding, clientsById),
  ),
});

const getClientsById = async () => {
  const response = await AlertVaultService.Default.listAlertVaultClients();
  return new Map(response.data.map(client => [client.id, client]));
};

const mapAlertToNotification = (alert: AlertVaultAlert): AlertResponse =>
  ({
    uuid: alert.alert_id,
    account_id: alert.account_id,
    environment_id: alert.environment_id,
    cloud: alert.cloud,
    region: alert.region,
    resource: alert.resource_type,
    entity_uuid: alert.entity_uuid,
    entity_id: alert.entity_id,
    entity_name: alert.entity_name,
    entity_type: alert.resource_type,
    alert_type: legacyEnumValue(
      alert.alert_type,
    ) as AlertResponse['alert_type'],
    alert_category: alert.category,
    category_class: alert.category_class,
    alert_rule_id: alert.policy_id,
    policy_id: alert.policy_id,
    severity: legacyEnumValue(alert.severity) as AlertResponse['severity'],
    message: alert.message ?? '',
    remediation: alert.remediation,
    operation_status: legacyEnumValue(
      alert.operation,
    ) as AlertResponse['operation_status'],
    status: legacyEnumValue(alert.status) as AlertResponse['status'],
    snooze: alert.snoozed,
    due_date: alert.due_date,
    extras: alert.extras as AlertResponse['extras'],
    last_seen: alert.last_seen,
    created_at: alert.first_seen,
    updated_at: alert.updated_at,
  } as AlertResponse);

const mapTrendToLegacyHistory = (items: Array<any>) => {
  const alertHistory: Record<string, Record<string, number>> = {};
  const createdHistory: Record<string, Record<string, number>> = {};
  const resolvedHistory: Record<string, Record<string, number>> = {};
  items.forEach(item => {
    const day = item.trend_for_date ?? item.day;
    if (!day) return;
    const created = Number(item.new_alerts ?? 0);
    const resolved = Number(item.resolved_alerts ?? 0);
    const active = Number(item.active_alerts ?? 0);
    // The Alert Vault daily projection is intentionally severity-agnostic.
    // Keep the legacy chart contract while exposing the exact total in HIGH;
    // consumers that need severity detail use the summary endpoint.
    alertHistory[day] = { CRITICAL: 0, HIGH: active, MEDIUM: 0, LOW: 0 };
    createdHistory[day] = { CRITICAL: 0, HIGH: created, MEDIUM: 0, LOW: 0 };
    resolvedHistory[day] = { CRITICAL: 0, HIGH: resolved, MEDIUM: 0, LOW: 0 };
  });
  return {
    alert_history: alertHistory,
    created_history: createdHistory,
    resolved_history: resolvedHistory,
  };
};

const mapAlertGroupToLegacy = (group: any, index: number) => ({
  group_id: `${group.dimension}:${group.value}`,
  description: group.value,
  alert_category: group.dimension === 'category' ? group.value : undefined,
  resource_types:
    group.dimension === 'resource_type' ? [group.value] : undefined,
  severity: mapAlertGroupSeverity(group),
  total_results: group.total,
  active_count: group.active,
  in_progress_count: group.in_progress,
  resolved_count: group.resolved,
  last_seen: group.last_seen,
  // Stable fallback for table keys when a dimension value is repeated in a
  // response due to a future backend grouping extension.
  _alertVaultGroupIndex: index,
});

const mapAlertGroupSeverity = (group: any) => {
  const counts = group.severity_counts;
  if (!counts) return { INFORMATIONAL: Number(group.total ?? 0) };
  return {
    CRITICAL: Number(counts.CRITICAL ?? 0),
    HIGH: Number(counts.HIGH ?? 0),
    MEDIUM: Number(counts.MEDIUM ?? 0),
    LOW: Number(counts.LOW ?? 0),
    INFORMATIONAL: Number(counts.INFORMATIONAL ?? 0),
  };
};

const mapAlertAggregateToLegacy = (
  aggregate: AlertVaultAggregate,
  index: number,
) => {
  const severity = {
    CRITICAL: Number(aggregate.severity_counts?.CRITICAL ?? 0),
    HIGH: Number(aggregate.severity_counts?.HIGH ?? 0),
    MEDIUM: Number(aggregate.severity_counts?.MEDIUM ?? 0),
    LOW: Number(aggregate.severity_counts?.LOW ?? 0),
    INFORMATIONAL: Number(aggregate.severity_counts?.INFORMATIONAL ?? 0),
  };
  const policyIds = Array.from(
    new Set([
      ...(aggregate.policy_ids ?? []),
      ...(aggregate.policy_id ? [aggregate.policy_id] : []),
    ]),
  );
  // rule_ids (check codes such as NO-TAGS) are newer than the generated
  // 0.1.10 client types; compliance frameworks and remediation key on them.
  const ruleIds: string[] =
    (aggregate as { rule_ids?: string[] }).rule_ids ?? [];
  // A finding row whose alerts are not backfilled yet falls back to its policy
  // as the group key; list those alerts by policy instead of group.
  const groupBy =
    aggregate.group_by === 'finding' &&
    policyIds.length === 1 &&
    aggregate.group_key === policyIds[0]
      ? 'policy'
      : aggregate.group_by;
  const accountIds = Array.from(
    new Set([
      ...(aggregate.account_ids ?? []),
      ...(aggregate.account_id ? [aggregate.account_id] : []),
    ]),
  );
  const active = Number(aggregate.status_counts?.ACTIVE ?? 0);
  const inProgress = Number(aggregate.status_counts?.IN_PROGRESS ?? 0);
  const resolved = Number(aggregate.status_counts?.RESOLVED ?? 0);
  return {
    group_id: `${groupBy}:${aggregate.group_key}`,
    description:
      aggregate.description ?? aggregate.entity_name ?? aggregate.group_key,
    alert_category: aggregate.category,
    category_class: aggregate.category_class,
    resource_types: aggregate.resource_type
      ? [aggregate.resource_type]
      : undefined,
    resource: aggregate.resource_type,
    entity_uuid: aggregate.entity_uuid ?? aggregate.resource_uuid,
    entity_id: aggregate.entity_id ?? aggregate.resource_uuid,
    entity_name: aggregate.entity_name ?? aggregate.description,
    account_id: aggregate.account_id,
    account_id_list: accountIds,
    environment_id: aggregate.environment_id,
    cloud: aggregate.cloud,
    region: aggregate.region,
    // Match the legacy contract: alert_rule_ids are check codes and
    // policy_ids are policy UUIDs (exceptions send policy_ids as rule_ids).
    alert_rule_id: ruleIds[0] ?? aggregate.policy_id ?? policyIds[0],
    alert_rule_ids: ruleIds,
    policy_id: aggregate.policy_id ?? policyIds[0],
    policy_ids: policyIds,
    blueprint_ids: aggregate.blueprint_ids ?? [],
    blueprints: [],
    severity,
    total_results: Number(aggregate.total ?? 0),
    active_count: active,
    in_progress_count: inProgress,
    resolved_count: resolved,
    last_seen: aggregate.last_seen,
    _alertVaultGroupIndex: index,
  };
};

// Alert Vault ticket fields newer than the generated 0.1.10 client types.
type AlertVaultTicketRecord = Ticket & {
  browse_link?: string;
  due_date?: string;
  scope?: string;
  resource_name?: string;
  resource_id?: string;
  resource_type?: string;
  account_id?: string;
  severity?: string;
};

// Legacy ticket rows show the Jira key as ticket_id and the provider as
// ticket_client; Alert Vault's own id stays available as alert_vault_ticket_id.
const mapTicketToLegacy = (ticket: Ticket): Record<string, any> => {
  const record = ticket as AlertVaultTicketRecord;
  return {
    ...ticket,
    alert_vault_ticket_id: ticket.ticket_id,
    ticket_id: ticket.external_id || ticket.ticket_id,
    ticket_client: ticket.external_id ? 'jira' : undefined,
    client_id: ticket.client_id,
    project: ticket.project,
    state: ticket.status,
    issue: ticket.summary,
    resource_id: record.resource_id || ticket.alert_id,
    resource_name: record.resource_name || undefined,
    resource_type: record.resource_type,
    account_id: record.account_id,
    severity: record.severity,
    due_date: record.due_date || undefined,
    created_at: ticket.created_at,
    last_sync_time: ticket.last_synced_at ?? ticket.updated_at,
    browse_link: record.browse_link || undefined,
  };
};

const mapTicketPageToLegacy = (
  page: TicketPage,
  input: AlertVaultAlertQuery,
) => ({
  tickets: page.items.map(mapTicketToLegacy),
  ticket_states: [],
  total: pageTotal(input, page.items.length, !!page.next_cursor),
  page: input.pageNumber ?? 1,
  page_size: input.pageSize ?? 50,
  pages: page.next_cursor ? (input.pageNumber ?? 1) + 1 : input.pageNumber ?? 1,
});

const mapActivityToLegacy = (page: AlertActivityPage) =>
  page.items.map(item => ({
    alert_id: item.alert_id,
    logs: [
      {
        alert_id: item.alert_id,
        event: item.event,
        actor: item.actor,
        occurred_at: item.occurred_at,
        metadata: item.metadata,
      },
    ],
  }));

const mapDeliveryToLegacy = (page: AlertDeliveryPage) =>
  page.items.map(item => ({
    alert_id: item.alert_id,
    workflow_status: { [item.channel]: item.status },
    alerts_on_resource: 0,
    delivery: item,
  }));

export const AlertVaultClientService = {
  list: async (provider?: string | null) => {
    const response = await AlertVaultService.Default.listAlertVaultClients({
      provider: canonicalProviderName(provider) ?? undefined,
    });

    return {
      ...response,
      data: response.data.map(mapClientToNotification) as any,
    };
  },
  get: async (clientId: string) => {
    const response = await AlertVaultService.Default.getAlertVaultClient({
      clientId,
    });
    return { ...response, data: mapClientToNotification(response.data) };
  },
  create: (request: any) =>
    AlertVaultService.Default.createAlertVaultClient({
      alertVaultClientInput: toAlertVaultClientInput(request),
    }),
  update: (request: any) =>
    AlertVaultService.Default.updateAlertVaultClient({
      clientId: request.uuid,
      alertVaultClientInput: toAlertVaultClientInput(request),
    }),
  delete: (clientId: string) =>
    AlertVaultService.Default.deleteAlertVaultClient({ clientId }),
};

export const AlertVaultWorkflowService = {
  list: async (activeOnly = false, clientId?: string) => {
    const [response, clientsById] = await Promise.all([
      AlertVaultService.Default.listAlertVaultWorkflows({
        activeOnly,
        clientId,
      }),
      getClientsById(),
    ]);

    return {
      ...response,
      data: response.data.map(workflow =>
        mapWorkflowToNotification(workflow, clientsById),
      ) as any,
    };
  },
  get: async (workflowId: string) => {
    const [response, clientsById] = await Promise.all([
      AlertVaultService.Default.getAlertVaultWorkflow({ workflowId }),
      getClientsById(),
    ]);
    return {
      ...response,
      data: mapWorkflowToNotification(response.data, clientsById),
    };
  },
  create: async (request: any) => {
    const clients = responseData(
      await AlertVaultService.Default.listAlertVaultClients(),
    );
    return AlertVaultService.Default.createAlertVaultWorkflow({
      alertWorkflowInput: toAlertVaultWorkflowInput(request, clients),
    });
  },
  update: async (request: any) => {
    const clients = responseData(
      await AlertVaultService.Default.listAlertVaultClients(),
    );
    return AlertVaultService.Default.updateAlertVaultWorkflow({
      workflowId: request.workflowId,
      alertWorkflowInput: toAlertVaultWorkflowInput(request, clients),
    });
  },
  delete: (workflowId: string) =>
    AlertVaultService.Default.deleteAlertVaultWorkflow({ workflowId }),
};

export const AlertVaultAlertService = {
  list: async (input: AlertVaultAlertQuery = {}) => {
    const cursor = cursorForPage('alerts', input);
    const response = await AlertVaultService.Default.listAlertVaultAlerts(
      {
        accountId: input.accountId,
        environmentId: input.environmentId,
        resourceType: input.resourceType,
        category: input.category,
        categoryClass: input.categoryClass,
        policyId: input.policyId,
        severity: input.severity
          ? Array.isArray(input.severity)
            ? input.severity
            : [input.severity]
          : undefined,
        status: input.status?.toLowerCase(),
        search: input.search,
        from: input.from,
        to: input.to,
        limit: input.pageSize,
        cursor,
      },
      // group_id and sorting are newer than the generated 0.1.10 client; Axios
      // query params are equivalent on the wire.
      alertListParams(input),
    );
    const page = responseData(response) as AlertVaultPage<AlertVaultAlert>;
    rememberNextCursor('alerts', input, page.next_cursor);
    return {
      ...response,
      data: {
        results: page.items.map(mapAlertToNotification),
        total_results: alertPageTotal(input, page),
      },
    };
  },
  get: async (alertId: string) => {
    const response = await AlertVaultService.Default.getAlertVaultAlert({
      alertId,
    });
    return { ...response, data: mapAlertToNotification(response.data) };
  },
  resourceAlerts: async (
    resourceId: string,
    input: AlertVaultAlertQuery = {},
  ) => {
    const response = await AlertVaultService.Default.listAlertVaultResourceAlerts(
      {
        resourceId,
        accountId: input.accountId,
        environmentId: input.environmentId,
        status: input.status?.toLowerCase(),
        limit: input.pageSize,
        cursor: cursorForPage(`resource:${resourceId}`, input),
      },
    );
    const page = responseData(response) as AlertVaultPage<AlertVaultAlert>;
    rememberNextCursor(`resource:${resourceId}`, input, page.next_cursor);
    return {
      ...response,
      data: {
        results: page.items.map(mapAlertToNotification),
        total_results: alertPageTotal(input, page),
      },
    };
  },
  history: async (input: any) => {
    const category = input.alertCategory ?? input.category;
    const response = await AlertVaultService.Default.getAlertTrends(
      {
        accountId: input.accountId,
        environmentId: input.environmentId,
        from: input.from ?? input.startDate,
        to: input.to ?? input.endDate,
      },
      category
        ? {
            // Keep category filtering working with the currently deployed 0.1.8
            // client. The generated 0.1.9 client will expose category as a typed
            // request field; Axios query params are equivalent on the wire.
            params: { category },
          }
        : undefined,
    );
    return { ...response, data: mapTrendToLegacyHistory(response.data.items) };
  },
  summary: async (input: AlertVaultAlertQuery = {}) => {
    const response = await AlertVaultService.Default.getAlertVaultSummary({
      accountId: input.accountId,
      environmentId: input.environmentId,
      resourceType: input.resourceType,
      category: input.category,
      categoryClass: input.categoryClass,
      severity: Array.isArray(input.severity)
        ? input.severity[0]
        : input.severity,
      status: input.status?.toLowerCase() as any,
    });
    const summary: AlertSummary = responseData(response);
    return {
      ...response,
      data: {
        ...summary,
        severity_count: {
          critical: summary.by_severity?.CRITICAL ?? 0,
          high: summary.by_severity?.HIGH ?? 0,
          medium: summary.by_severity?.MEDIUM ?? 0,
          low: summary.by_severity?.LOW ?? 0,
          snoozed: 0,
          assets: summary.total,
        },
      },
    };
  },
  groups: async (input: AlertVaultAlertQuery = {}) => {
    const response = await AlertVaultService.Default.listAlertVaultGroups({
      groupBy: (input.groupBy ?? 'category') as any,
      accountId: input.accountId,
      environmentId: input.environmentId,
      resourceType: input.resourceType,
      category: input.category,
      categoryClass: input.categoryClass,
      severity: Array.isArray(input.severity)
        ? input.severity[0]
        : input.severity,
      status: input.status?.toLowerCase() as any,
      limit: input.pageSize,
      cursor: cursorForPage('groups', input),
    });
    const page: AlertGroupPage = responseData(response);
    rememberNextCursor('groups', input, page.next_cursor);
    return {
      ...response,
      data: {
        page_number: input.pageNumber ?? 1,
        total_results: pageTotal(input, page.items.length, !!page.next_cursor),
        aggregated_alerts: page.items.map(mapAlertGroupToLegacy),
      },
    };
  },
  aggregates: async (input: AlertVaultAlertQuery = {}) => {
    const response = await AlertVaultService.Default.listAlertVaultAggregates({
      // 'finding' groups by PolicyHub group_id; the generated 0.1.10 client
      // only types resource | policy.
      groupBy: (input.groupBy ?? 'resource') as 'resource' | 'policy',
      accountId: input.accountId,
      environmentId: input.environmentId,
      resourceType: input.resourceType,
      category: input.category,
      categoryClass: input.categoryClass,
      policyId: input.policyId,
      severity: Array.isArray(input.severity)
        ? input.severity[0]
        : input.severity,
      status: input.status?.toLowerCase(),
      search: input.search,
      from: input.from,
      to: input.to,
      limit: input.pageSize,
      cursor: cursorForPage('aggregates', input),
    });
    const page = responseData(response) as {
      items: AlertVaultAggregate[];
      next_cursor?: string;
    };
    rememberNextCursor('aggregates', input, page.next_cursor);
    // Alert Vault now returns the exact group total; older servers fall back
    // to the lower-bound estimate.
    const total = (page as { total?: number }).total;
    return {
      ...response,
      data: {
        page_number: input.pageNumber ?? 1,
        total_results:
          typeof total === 'number'
            ? total
            : pageTotal(input, page.items.length, !!page.next_cursor),
        aggregated_alerts: page.items.map(mapAlertAggregateToLegacy),
      },
    };
  },
  categories: async (input: AlertVaultAlertQuery = {}) => {
    // The legacy findings filters are keyed by category class, while the
    // Alert Vault categories endpoint is intentionally a flat raw-category
    // view. Fetch the four bounded class slices in parallel so the adapter
    // preserves the old semantic index without adding a second data model.
    const responses = await Promise.all(
      alertCategoryClasses.map(categoryClass =>
        AlertVaultService.Default.listAlertVaultGroups({
          groupBy: 'category',
          accountId: input.accountId,
          environmentId: input.environmentId,
          categoryClass,
          limit: 1000,
        }),
      ),
    );
    const summary: Record<string, any[]> = {};
    responses.forEach((response, index) => {
      const page: AlertGroupPage = responseData(response);
      const categoryClass = alertCategoryClasses[index];
      summary[categoryClass] = page.items.map(group => ({
        category: group.value,
        severity: mapAlertGroupSeverity(group),
      }));
    });
    const categories: Record<string, any> = {};
    Object.entries(summary).forEach(([categoryClass, groups]) => {
      groups.forEach(group => {
        const existing = categories[group.category];
        if (!existing) {
          categories[group.category] = {
            ...group,
            category_class: categoryClass,
          };
          return;
        }
        const severityKeys = [
          'CRITICAL',
          'HIGH',
          'MEDIUM',
          'LOW',
          'INFORMATIONAL',
        ];
        const severity = severityKeys.reduce(
          (counts, key) => ({
            ...counts,
            [key]:
              Number(existing.severity?.[key] ?? 0) +
              Number(group.severity?.[key] ?? 0),
          }),
          {},
        );
        categories[group.category] = {
          ...existing,
          total: Number(existing.total ?? 0) + Number(group.total ?? 0),
          severity,
        };
      });
    });
    return {
      ...responses[0],
      data: { summary, categories },
    };
  },
  resourceTypes: async (input: AlertVaultAlertQuery = {}) => {
    const response = await AlertVaultService.Default.listAlertVaultGroups({
      groupBy: 'resource_type',
      accountId: input.accountId,
      environmentId: input.environmentId,
      limit: input.pageSize,
      cursor: cursorForPage('resource-types', input),
    });
    const page: AlertGroupPage = responseData(response);
    rememberNextCursor('resource-types', input, page.next_cursor);
    return {
      ...response,
      data: {
        resources: page.items.map(group => ({
          resource_type: group.value,
          alert_count: group.total,
        })),
      },
    };
  },
  topIssues: async (input: AlertVaultAlertQuery = {}) => {
    const response = await AlertVaultService.Default.listAlertVaultTopIssues({
      accountId: input.accountId,
      environmentId: input.environmentId,
    });
    const page: AlertGroupPage = responseData(response);
    return {
      ...response,
      data: {
        top_issues_by_count: page.items.map(mapAlertGroupToLegacy),
        top_issues_old: page.items.map(mapAlertGroupToLegacy),
        top_issues_severity: page.items.map(mapAlertGroupToLegacy),
      },
    };
  },
  activity: async (alertId: string, cursor?: string, limit?: number) => {
    const response = await AlertVaultService.Default.listAlertVaultActivity({
      alertId,
      cursor,
      limit,
    });
    return { ...response, data: mapActivityToLegacy(responseData(response)) };
  },
  allDeliveries: async (cursor?: string, limit?: number) => {
    const response = await AlertVaultService.Default.listAllAlertVaultDeliveries(
      {
        cursor,
        limit,
      },
    );
    return { ...response, data: mapDeliveryToLegacy(responseData(response)) };
  },
  deliveries: async (alertId: string, cursor?: string, limit?: number) => {
    const response = await AlertVaultService.Default.listAlertVaultDeliveries({
      alertId,
      cursor,
      limit,
    });
    return { ...response, data: mapDeliveryToLegacy(responseData(response)) };
  },
  deliveryStatus: async (
    input: { alertId?: string; cursor?: string; limit?: number } = {},
  ) => {
    const response = await AlertVaultService.Default.listAlertVaultDeliveryStatus(
      {
        alertId: input.alertId,
        cursor: input.cursor,
        limit: input.limit,
      },
    );
    return { ...response, data: mapDeliveryToLegacy(responseData(response)) };
  },
  ruleSummary: async (ruleId: string, input: AlertVaultAlertQuery = {}) => {
    const response = await AlertVaultService.Default.getAlertVaultRuleSummary({
      ruleId,
      status: input.status?.toLowerCase() as any,
    });
    const summary: AlertSummary = responseData(response);
    return {
      ...response,
      data: [
        {
          rule_id: ruleId,
          alert_count: summary.total,
        },
      ],
    };
  },
  ruleSummaries: async (input: any) => {
    const request = input?.ruleIdSummaryRequest ?? input ?? {};
    const ruleIds: string[] = request.rule_ids ?? [];
    if (ruleIds.length === 0) return { data: [] };
    const response = await AlertVaultService.Default.listAlertVaultAggregates({
      groupBy: 'policy',
      accountId: request.account_id,
      environmentId: request.environment_id,
      limit: 1000,
    });
    const page = responseData(response) as AlertVaultPage<AlertVaultAggregate>;
    const counts = new Map(
      page.items.map(item => [item.group_key, item.total]),
    );
    const summaries = ruleIds.map(ruleId => ({
      rule_id: ruleId,
      alert_count: counts.get(ruleId) ?? 0,
    }));
    return { data: summaries };
  },
  lifecycle: (
    alertId: string,
    event: MutateAlertLifecycleRequest['event'],
    details: Record<string, any> = {},
  ) =>
    AlertVaultService.Default.mutateAlertLifecycle({
      alertId,
      idempotencyKey: uuidv4(),
      mutateAlertLifecycleRequest: {
        event,
        ...details,
        ...(typeof details.due_date === 'number'
          ? {
              due_date: new Date(
                Date.now() + details.due_date * 60_000,
              ).toISOString(),
            }
          : {}),
      },
    }),
};

export const AlertVaultTicketService = {
  list: async (input: AlertVaultAlertQuery = {}) => {
    const response = await AlertVaultService.Default.listAlertVaultTickets({
      accountId: input.accountId,
      environmentId: input.environmentId,
      clientId: (input as any).clientId,
      alertId: input.alertId,
      status: input.status,
      cursor: cursorForPage('tickets', input),
      limit: input.pageSize,
    });
    const page: TicketPage = responseData(response);
    rememberNextCursor('tickets', input, page.next_cursor);
    return { ...response, data: mapTicketPageToLegacy(page, input) };
  },
  get: (ticketId: string) =>
    AlertVaultService.Default.getAlertVaultTicket({ ticketId }),
  create: (request: any) =>
    AlertVaultService.Default.createAlertVaultTicket({
      idempotencyKey: uuidv4(),
      ticketInput: {
        alert_id: request?.ticketCreateRequest?.alert_uuid ?? request.alert_id,
        client_id: request?.ticketCreateRequest?.client_id ?? request.client_id,
        summary: request?.ticketCreateRequest?.ticket_create_fields?.summary,
        fields:
          request?.ticketCreateRequest?.ticket_create_fields ?? request.fields,
      } as TicketInput,
    }),
  update: (ticketId: string, update: TicketUpdate) =>
    AlertVaultService.Default.updateAlertVaultTicket({
      ticketId,
      ticketUpdate: update,
    }),
  sync: (ticketId?: string, clientId?: string) =>
    (ticketId
      ? AlertVaultService.Default.synchronizeAlertVaultTicket({ ticketId })
      : AlertVaultService.Default.synchronizeAlertVaultTickets({ clientId })
    ).then(response => ({
      ...response,
      data: { total_synced: 0, synced_tickets: [], failed_tickets: [] },
    })),
  filters: async () => {
    const response = await AlertVaultService.Default.getAlertVaultTicketFilters();
    const data = responseData(response) as any;
    return {
      ...response,
      data: {
        assignees: (data.assignee_ids ?? []).map((assignee_id: string) => ({
          assignee_id,
        })),
      },
    };
  },
};

export const AlertVaultDeliveryService = {
  create: (input: DeliveryInput) =>
    AlertVaultService.Default.createAlertVaultDelivery({
      idempotencyKey: uuidv4(),
      deliveryInput: input,
    }),
};
